import { useTranslation } from 'react-i18next';

// The home page's demos, drawn from the real exercise library (meu-treino-api src/seed/new-exercise-db-data.json):
// each exercise's names in both languages, its muscle group and equipment category, its thumbnail on R2 and, for the
// working phone in the hero, its how-to steps. The loads and calories are a demo account's, shown as examples.

export const THUMBS_URL = 'https://media.meutreino.fit/exercise-thumbnails/';

/** The site's language and an item's name in it: "Supino Reto com Barra", "Bench Press (Barbell)" */
export const useLanding = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'pt' ? 'pt' : 'en';
  return { t, language, name: (item) => item[language] };
};

/** The weight unit each site shows: myworkoutskill.com reads loads in pounds */
export const unitFor = (language) => (language === 'pt' ? 'kg' : 'lbs');

/** "42.5" as each site reads it: "42,5" in pt; thousands as "1.101" and "1,101" */
export const formatNumber = (value, language) =>
  Number(value).toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US', { maximumFractionDigits: 1 });

/** "40 kg", "95 lbs" */
export const formatLoad = (load, language) => `${formatNumber(load[unitFor(language)], language)} ${unitFor(language)}`;

/** 90 → "1:30" */
export const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.max(0, seconds) % 60).padStart(2, '0')}`;

/** Treino A, chest and triceps, as the guided session in the hero runs it */
export const HERO_WORKOUT = [
  {
    id: "79D0BB3A",
    pt: "Supino Reto com Barra",
    en: "Bench Press (Barbell)",
    muscle: "chest",
    equipment: "barbell",
    file: "00251201-Barbell-Bench-Press_Chest_thumbnail@3x.jpg",
    sets: 4,
    reps: 8,
    rest: 90,
    load: { kg: 40, lbs: 95 },
    steps: {
      pt: [
        "Deite-se no banco.",
        "Estenda os braços e segure a barra de forma simétrica, com as mãos um pouco mais afastadas que a largura dos ombros.",
        "Traga as escápulas para trás e pressione-as contra o banco.",
        "Arqueie a lombar e apoie os pés firmemente no chão.",
        "Inspire, tire a barra do suporte e posicione-a sobre o peito.",
        "Inspire novamente e abaixe a barra até a parte inferior do peito, encostando-a levemente.",
        "Segure por um momento e empurre a barra para cima até estender totalmente os cotovelos. Expire.",
      ],
      en: [
        "Lie on the bench.",
        "Extend your arms and grab the bar evenly, having your hands slightly wider than shoulder-width apart.",
        "Bring your shoulder blades back and dig them into the bench.",
        "Arch your lower back and plant your feet flat on the floor.",
        "Take a breath, unrack the bar, and bring it over your chest.",
        "Inhale again and lower the barbell to your lower chest, tapping it slightly.",
        "Hold for a moment and press the bar until your elbows are straight. Exhale.",
      ],
    },
  },
  {
    id: "07B38369",
    pt: "Supino Inclinado com Halteres",
    en: "Incline Bench Press (Dumbbell)",
    muscle: "chest",
    equipment: "dumbbell",
    file: "03141201-Dumbbell-Incline-Bench-Press_Chest_thumbnail@3x.jpg",
    sets: 3,
    reps: 10,
    rest: 75,
    load: { kg: 14, lbs: 30 },
    steps: {
      pt: [
        "Ajuste a inclinação de um banco para 45 graus, pegue um par de halteres e sente-se.",
        "Apoie os halteres sobre as coxas e deite-se, impulsionando os pesos com as coxas até posicioná-los sobre o tronco.",
        "Leve os halteres para as laterais do tronco, apoie os pés no chão, retraia os ombros e inspire. Mantenha os cotovelos um pouco próximos ao corpo.",
        "Empurre os halteres em direção ao teto, encostando um no outro levemente no alto. Expire.",
        "Abaixe os pesos para as laterais, inspirando na descida.",
      ],
      en: [
        "Set the incline of a bench at 45 degrees, grab a pair of dumbbells, and sit down.",
        "Place the dumbbells on top of your thighs, and lie back as you kick the weights up and over your torso.",
        "Bring the dumbbells to your sides, plant your feet on the floor, retract your shoulders, and take a breath. Keep your elbows somewhat tucked in.",
        "Press both dumbbells toward the ceiling, tapping them lightly at the top. Breathe out.",
        "Lower the weights to your sides, breathing in on the way down.",
      ],
    },
  },
  {
    id: "D3E2AB55",
    pt: "Crucifixo Inclinado com Halteres",
    en: "Incline Chest Fly (Dumbbell)",
    muscle: "chest",
    equipment: "dumbbell",
    file: "03191201-Dumbbell-Incline-Fly_Chest_thumbnail@3x.jpg",
    sets: 3,
    reps: 12,
    rest: 60,
    load: { kg: 8, lbs: 20 },
    steps: {
      pt: [
        "Pegue um par de halteres e sente-se na ponta de um banco inclinado.",
        "Apoie os pesos sobre as coxas e deite-se lentamente.",
        "Levante os halteres sobre o peito e estenda os braços.",
        "Traga os ombros para trás e inspire.",
        "Abra os braços para os lados, mantendo os cotovelos levemente flexionados.",
        "Desça até que os cotovelos fiquem na altura do tronco.",
        "Feche os braços contraindo o peitoral e encoste os halteres no alto. Expire.",
      ],
      en: [
        "Grab a pair of dumbbells and sit at the base of an incline gym bench.",
        "Place the weights on top of your thighs and lie back slowly.",
        "Lift the dumbbells over your chest and extend your arms.",
        "Bring your shoulders back and take a breath.",
        "Extend your arms to your sides until your elbows are at torso level. Keep your elbows slightly bent.",
        "Bring your arms in, squeezing your chest, and tap the dumbbells at the top. Exhale.",
      ],
    },
  },
  {
    id: "651F844C",
    pt: "Crossover na Polia",
    en: "Cable Fly Crossovers",
    muscle: "chest",
    equipment: "machine",
    file: "12691201-Cable-Standing-Up-Straight-Crossovers_Chest_thumbnail@3x.jpg",
    sets: 3,
    reps: 12,
    rest: 60,
    load: { kg: 15, lbs: 30 },
    steps: {
      pt: [
        "Ajuste a carga nas duas pilhas de pesos do crossover.",
        "Coloque as polias na posição mais alta e prenda um pegador em cada uma.",
        "Segure um pegador de cada vez e posicione-se no centro, entre as duas torres.",
        "Abra os braços para os lados e dê meio passo à frente para tirar a carga das pilhas.",
        "Contraia o abdômen, traga os ombros para trás e inspire.",
        "Leve os braços para dentro e para baixo até os nós dos dedos se encontrarem na frente do quadril. Expire.",
        "Segure por um momento e volte os braços para os lados enquanto inspira.",
      ],
      en: [
        "Adjust the load on the pair of weight stacks inside the cable crossover machine.",
        "Set the pulleys in the highest position and attach handles on both.",
        "Grab the handles one at a time and position yourself in the middle between the two pillars.",
        "Bring your arms to your sides and take a half step forward to lift the weights from their stacks.",
        "Engage your abs, bring your shoulders back, and inhale.",
        "Bring your arms in and down, meeting your knuckles in front of your hips. Exhale.",
        "Hold for a moment and bring your arms to your sides as you breathe in.",
      ],
    },
  },
  {
    id: "93A552C6",
    pt: "Tríceps Pulley",
    en: "Triceps Pushdown",
    muscle: "triceps",
    equipment: "machine",
    file: "02011201-Cable-Pushdown_Upper-Arms_thumbnail@3x.jpg",
    sets: 3,
    reps: 12,
    rest: 60,
    load: { kg: 20, lbs: 40 },
    steps: {
      pt: [
        "Escolha a carga apropriada, coloque a polia na posição mais alta e prenda uma barra.",
        "Segure a barra com pegada pronada, de forma simétrica, e mantenha os cotovelos junto ao corpo.",
        "Dê alguns passos para trás e incline ligeiramente o tronco para frente.",
        "Contraia o abdômen, retraia as escápulas e inspire.",
        "Estenda os braços mantendo os cotovelos ao lado do corpo e segure por um momento. Expire.",
        "Dobre lentamente os braços até que os pulsos fiquem um pouco acima dos cotovelos, inspirando enquanto os braços flexionam.",
      ],
      en: [
        "Pick the appropriate load, set the pulley in the highest position, and attach a bar.",
        "Grab the bar with an even overhand grip and bring your elbows to your sides.",
        "Take a couple of steps back and lean your torso slightly forward.",
        "Engage your abs, retract your shoulder blades, and take a breath.",
        "Extend your arms while keeping your elbows to your sides and hold for a moment. Exhale.",
        "Slowly bend your arms until your wrists are slightly higher than your elbows. Breathe in as your arms bend.",
      ],
    },
  },
  {
    id: "875F585F",
    pt: "Tríceps Testa com Barra",
    en: "Skullcrusher (Barbell)",
    muscle: "triceps",
    equipment: "barbell",
    file: "00601201-Barbell-Lying-Triceps-Extension-Skull-Crusher_Upper-Arms_thumbnail_@3x.jpg",
    sets: 3,
    reps: 10,
    rest: 60,
    load: { kg: 15, lbs: 35 },
    steps: {
      pt: [
        "Monte a carga em uma barra reta, tire-a do chão e segure-a na frente do peito.",
        "Sente-se com cuidado em um banco reto e deite-se mantendo a barra próxima ao tronco.",
        "Estenda os braços e traga os ombros para trás. Apoie os pés no chão.",
        "Inspire e abaixe a barra até a testa ou até atrás da cabeça.",
        "Faça uma pausa e estenda os braços mantendo os cotovelos na posição. Expire perto do topo.",
      ],
      en: [
        "Load a straight bar, lift it off the floor, and support it in front of your chest.",
        "Carefully sit on a flat gym bench and lie back while keeping the bar close to your torso.",
        "Extend your arms and bring your shoulders back. Plant your feet on the floor.",
        "Take a breath and lower the barbell to your forehead or behind your head.",
        "Pause for a moment and extend your arms while keeping your elbows in position. Exhale near the top.",
      ],
    },
  },
];

// Treino B as the AI card shows it built: back and biceps, with dumbbells, moderate volume
export const AI_WORKOUT = [
  { pt: 'Remada Serrote com Halter', en: 'Dumbbell Row', muscle: 'lats', equipment: 'dumbbell', file: '02921201-Dumbbell-Bent-over-Row_Back_thumbnail@3x.jpg', sets: 4, reps: 10, rest: 90, load: { kg: 14, lbs: 30 } },
  { pt: 'Remada Curvada com Halteres', en: 'Bent Over Row (Dumbbell)', muscle: 'upper_back', equipment: 'dumbbell', file: '02931201-Dumbbell-Bent-Over-Row_Back_thumbnail@3x.jpg', sets: 4, reps: 10, rest: 90, load: { kg: 12, lbs: 25 } },
  { pt: 'Rosca Direta com Halteres', en: 'Bicep Curl (Dumbbell)', muscle: 'biceps', equipment: 'dumbbell', file: '02941201-Dumbbell-Biceps-Curl_Upper-Arms_thumbnail@3x.jpg', sets: 3, reps: 10, rest: 60, load: { kg: 10, lbs: 20 } },
  { pt: 'Rosca Martelo com Halteres', en: 'Hammer Curl (Dumbbell)', muscle: 'biceps', equipment: 'dumbbell', file: '03121201-Dumbbell-Hammer-Curl-(version-2)_Upper-Arms_thumbnail@3x.jpg', sets: 3, reps: 12, rest: 60, load: { kg: 10, lbs: 20 } },
];

// The library pane of the from-scratch card, filtered to legs: two already in the workout
export const LIBRARY_ROWS = [
  { pt: 'Agachamento com Barra', en: 'Squat (Barbell)', muscle: 'quadriceps', equipment: 'barbell', file: '00431201-Barbell-Full-Squat_Thighs_thumbnail@3x.jpg', added: true },
  { pt: 'Leg Press (Máquina)', en: 'Leg Press (Machine)', muscle: 'quadriceps', equipment: 'machine', file: '07391201-Sled-45-Leg-Press_Hips_thumbnail@3x.jpg' },
  { pt: 'Cadeira Extensora', en: 'Leg Extension (Machine)', muscle: 'quadriceps', equipment: 'machine', file: '05851201-Lever-Leg-Extension_Thighs_thumbnail@3x.jpg', added: true },
  { pt: 'Mesa Flexora', en: 'Lying Leg Curl (Machine)', muscle: 'hamstrings', equipment: 'machine', file: '05861201-Lever-Lying-Leg-Curl_Thighs_thumbnail@3x.jpg' },
];

// …and the workout being built from it: the squat closed, the leg extension open on its steppers
export const SCRATCH_WORKOUT = [
  { ...LIBRARY_ROWS[0], sets: 4, reps: 8, rest: 120, load: { kg: 60, lbs: 135 } },
  { ...LIBRARY_ROWS[2], sets: 3, reps: 12, rest: 60, load: { kg: 45, lbs: 100 } },
];

// Calorias do dia: a day of the demo plan (2.217 kcal), breakfast and lunch logged, the afternoon snack next
export const DAY_TARGET = { calories: 2217, protein: 123, carbs: 280, fat: 67 };
export const DAY_EATEN = { calories: 1101, protein: 74, carbs: 108, fat: 28 };
export const SNACK = {
  name: { pt: 'Lanche da tarde', en: 'Afternoon snack' },
  foods: [
    { pt: 'Iogurte natural', en: 'Plain yogurt', grams: 170, calories: 87, protein: 7, carbs: 7, fat: 5 },
    { pt: 'Granola', en: 'Granola', grams: 40, calories: 168, protein: 4, carbs: 27, fat: 5 },
    { pt: 'Mamão papaia', en: 'Papaya', grams: 150, calories: 60, protein: 1, carbs: 16, fat: 0 },
  ],
};
// The searched food that joins two of the plan's in one selection
export const SEARCHED_FOOD = { pt: 'Banana, prata, crua', en: 'Banana, raw', grams: 120, calories: 118 };
// The week's days as small rings (share of the target), today the fourth; the rest of the week hasn't happened
export const WEEK_SHARES = [0.98, 1, 0.86, null, 0, 0, 0];
export const WEEK_DATES = [10, 11, 12, 13, 14, 15, 16];
export const TODAY_INDEX = 3;

// The diet plan's plate for the same day: one arc per meal, sized by its calories
export const PLATE_MEALS = [
  { pt: 'Café da manhã', en: 'Breakfast', calories: 443, foods: 3, state: 'done' },
  { pt: 'Almoço', en: 'Lunch', calories: 658, foods: 4, state: 'done' },
  { pt: 'Lanche da tarde', en: 'Afternoon snack', calories: 315, foods: 3, state: 'next' },
  { pt: 'Jantar', en: 'Dinner', calories: 801, foods: 4, state: 'later' },
];

/**
 * "Já tem um plano do nutricionista?": bringing a nutritionist's plan in (a photo or PDF) to track its calories. On
 * since the import went live (29/10/2026); turning it off takes the block out of the page.
 */
export const SHOW_PLAN_IMPORT = true;

// Plano alimentar: the same demo account as Calorias do dia (man, 30, 82 kg, 1.78 m, moderate, losing weight), its
// numbers worked out as the app's questionnaire does (utils/nutrition.js) and its week as the plan built it
export const DIET_NUMBERS = { bmr: 1788, tdee: 2771, target: 2217 };
// Each day's calories in the plan, Monday first; Thursday (today) is the plate's day and adds up from its meals
export const DIET_WEEK = [2203, 2231, 2198, null, 2224, 2240, 2209];

/**
 * The foods of the demo, per 100 g as the food database (TACO) gives them: [kcal, protein, carbs, fat]. `plan` is the
 * name a plan writes, `db` the database's, which a food swapped in by hand takes. `kind` says which foods can take its
 * place (DIET_SWAPS). The salad is a plan's own item, not in the database.
 */
export const DIET_FOODS = {
  arrozBranco: { kind: 'carb', per100: [128, 2.5, 28.1, 0.2], plan: { pt: 'Arroz branco', en: 'White rice' }, db: { pt: 'Arroz, tipo 1, cozido', en: 'Rice, white, cooked' } },
  arrozIntegral: { kind: 'carb', per100: [124, 2.6, 25.8, 1.0], plan: { pt: 'Arroz integral', en: 'Brown rice' }, db: { pt: 'Arroz, integral, cozido', en: 'Rice, brown, cooked' } },
  batataDoce: { kind: 'carb', per100: [77, 0.6, 18.4, 0.1], plan: { pt: 'Batata-doce cozida', en: 'Boiled sweet potato' }, db: { pt: 'Batata, doce, cozida', en: 'Sweet potato, boiled' } },
  mandioca: { kind: 'carb', per100: [125, 0.6, 30.1, 0.3], plan: { pt: 'Mandioca cozida', en: 'Boiled cassava' }, db: { pt: 'Mandioca, cozida', en: 'Cassava, boiled' } },
  feijaoCarioca: { kind: 'bean', per100: [76, 4.8, 13.6, 0.5], plan: { pt: 'Feijão carioca', en: 'Pinto beans' }, db: { pt: 'Feijão, carioca, cozido', en: 'Beans, pinto, cooked' } },
  feijaoPreto: { kind: 'bean', per100: [77, 4.5, 14.0, 0.5], plan: { pt: 'Feijão preto', en: 'Black beans' }, db: { pt: 'Feijão, preto, cozido', en: 'Beans, black, cooked' } },
  feijaoRajado: { kind: 'bean', per100: [85, 5.5, 15.3, 0.4], plan: { pt: 'Feijão rajado', en: 'Cranberry beans' }, db: { pt: 'Feijão, rajado, cozido', en: 'Beans, cranberry, cooked' } },
  lentilha: { kind: 'bean', per100: [93, 6.3, 16.3, 0.5], plan: { pt: 'Lentilha', en: 'Lentils' }, db: { pt: 'Lentilha, cozida', en: 'Lentils, cooked' } },
  frango: { kind: 'protein', per100: [159, 32.0, 0, 2.5], plan: { pt: 'Peito de frango grelhado', en: 'Grilled chicken breast' }, db: { pt: 'Frango, peito, sem pele, grelhado', en: 'Chicken, breast, skinless, grilled' } },
  patinho: { kind: 'protein', per100: [219, 35.9, 0, 7.3], plan: { pt: 'Patinho grelhado', en: 'Grilled lean beef' }, db: { pt: 'Carne, bovina, patinho, sem gordura, grelhado', en: 'Beef, sirloin tip, lean, grilled' } },
  merluza: { kind: 'protein', per100: [122, 26.6, 0, 0.9], plan: { pt: 'Filé de merluza assado', en: 'Baked hake fillet' }, db: { pt: 'Merluza, filé, assado', en: 'Hake, fillet, baked' } },
  ovo: { kind: 'protein', per100: [146, 13.3, 0.6, 9.5], plan: { pt: 'Ovos cozidos', en: 'Boiled eggs' }, db: { pt: 'Ovo, de galinha, inteiro, cozido', en: 'Egg, whole, boiled' } },
  salada: { kind: 'veg', per100: [13, 1.2, 2.4, 0.2], plan: { pt: 'Salada de alface e tomate', en: 'Lettuce and tomato salad' }, db: { pt: 'Salada de alface e tomate', en: 'Lettuce and tomato salad' } },
  brocolis: { kind: 'veg', per100: [25, 2.1, 4.4, 0.5], plan: { pt: 'Brócolis cozido', en: 'Boiled broccoli' }, db: { pt: 'Brócolis, cozido', en: 'Broccoli, boiled' } },
  cenoura: { kind: 'veg', per100: [30, 0.8, 6.7, 0.2], plan: { pt: 'Cenoura cozida', en: 'Boiled carrots' }, db: { pt: 'Cenoura, cozida', en: 'Carrot, boiled' } },
  abobrinha: { kind: 'veg', per100: [15, 1.1, 3.0, 0.2], plan: { pt: 'Abobrinha cozida', en: 'Boiled zucchini' }, db: { pt: 'Abobrinha, italiana, cozida', en: 'Zucchini, boiled' } },
  chuchu: { kind: 'veg', per100: [19, 0.4, 4.8, 0], plan: { pt: 'Chuchu cozido', en: 'Boiled chayote' }, db: { pt: 'Chuchu, cozido', en: 'Chayote, boiled' } },
  azeite: { kind: 'fat', per100: [884, 0, 0, 100], plan: { pt: 'Azeite de oliva', en: 'Olive oil' }, db: { pt: 'Azeite, de oliva, extra virgem', en: 'Olive oil, extra virgin' } },
  abacate: { kind: 'fat', per100: [96, 1.2, 6.0, 8.4], plan: { pt: 'Abacate', en: 'Avocado' }, db: { pt: 'Abacate, cru', en: 'Avocado, raw' } },
  canola: { kind: 'fat', per100: [884, 0, 0, 100], plan: { pt: 'Óleo de canola', en: 'Canola oil' }, db: { pt: 'Óleo, de canola', en: 'Canola oil' } },
};

// The database foods offered in a food's place, by kind, in this order (the ones already in the meal are left out)
export const DIET_SWAPS = {
  carb: ['batataDoce', 'mandioca', 'arrozIntegral', 'arrozBranco'],
  bean: ['feijaoPreto', 'lentilha', 'feijaoRajado', 'feijaoCarioca'],
  protein: ['merluza', 'patinho', 'ovo', 'frango'],
  veg: ['cenoura', 'abobrinha', 'brocolis', 'chuchu'],
  fat: ['abacate', 'canola'],
};

/**
 * Thursday's lunch and the two "Trocar refeição" brings in turn: the same 658 kcal (the plate's lunch) and protein,
 * carbs and fat within 3 g of each other. A Brazilian plate, a second one with brown rice and beef, and a fish one,
 * as the plan's lunch archetypes go. `spoons`: the oil is written as teaspoons, not weighed.
 */
export const DIET_LUNCHES = [
  [
    { id: 'arrozBranco', grams: 180 },
    { id: 'feijaoCarioca', grams: 140 },
    { id: 'frango', grams: 170 },
    { id: 'salada', grams: 60 },
    { id: 'azeite', grams: 5, spoons: 1 },
  ],
  [
    { id: 'arrozIntegral', grams: 180 },
    { id: 'feijaoPreto', grams: 140 },
    { id: 'patinho', grams: 140 },
    { id: 'brocolis', grams: 80 },
  ],
  [
    { id: 'merluza', grams: 220 },
    { id: 'batataDoce', grams: 360 },
    { id: 'brocolis', grams: 100 },
    { id: 'azeite', grams: 10, spoons: 2 },
  ],
];

// A nutritionist's plan as it comes in (a photo of the page), with the calories the app finds for each line
export const IMPORT_PAPER = {
  pt: {
    title: 'Plano alimentar',
    meal: 'Café da manhã (7h)',
    lines: [
      { text: '2 fatias de pão integral', kcal: 126 },
      { text: '2 ovos mexidos', kcal: 150 },
      { text: '1 fatia média de mamão', kcal: 60 },
    ],
  },
  en: {
    title: 'Meal plan',
    meal: 'Breakfast (7 am)',
    lines: [
      { text: '2 slices of whole-wheat bread', kcal: 126 },
      { text: '2 scrambled eggs', kcal: 150 },
      { text: '1 medium slice of papaya', kcal: 60 },
    ],
  },
};

// Two lessons of the Central de Aprendizado (meu-treino-api src/api/academy-category/seed), per site
export const ACADEMY_VIDEOS = {
  pt: [
    { title: 'Tudo que você precisa saber sobre Push Pull Legs', channel: 'Samuel Meller' },
    { title: 'Guia de treino: seleção de exercícios', channel: 'Aielo' },
  ],
  en: [
    { title: 'How To Train For Pure Muscle Growth', channel: 'Jeff Nippard' },
    { title: 'The Best Science-Based Minimalist Workout Plan', channel: 'Jeremy Ethier' },
  ],
};
